import { useEffect, useState } from 'react'

import {
  TagManagerAction,
  TagManagerEventType,
} from '~/platform/utils/googleTagManager/tagManagerModels'
import { triggerTagManagerEvent } from '~/platform/utils/googleTagManager/tagManagerUtils'
import { HeroSourcingSelect } from '~/widgets/CostBomDemo/components/HeroSourcingSelect'
import { setProductImage } from '~/widgets/CostBomDemo/engine/heroEngine'
import { useTypedPlaceholder } from '~/widgets/CostBomDemo/hooks/useTypedPlaceholder'
import {
  COST_BOM_DEMO_STRINGS,
  SAMPLE_PRODUCTS,
} from '~/widgets/CostBomDemo/strings'

/** The product the example model opens on. */
const DEFAULT_PRODUCT = SAMPLE_PRODUCTS[0].product

/** Where a visitor's product is made, unless they say otherwise. */
const DEFAULT_COUNTRY = 'United States'

export interface ReportRequest {
  /** What the visitor described, trimmed. Empty if they sent without one. */
  product: string
  /** Where they said it is made. */
  sourcing: string
}

interface HeroComposerProps {
  onSubmit: (request: ReportRequest) => void
}

/**
 * The hero's way in: a description of the visitor's product and where it is
 * made. The model below is an example of what comes back.
 *
 * Send asks for an email to send a cost model to. It does not require a
 * description first; the email card asks for one if it is missing.
 */
export function HeroComposer({ onSubmit }: HeroComposerProps) {
  const [text, setText] = useState('')
  const [country, setCountry] = useState<string>(DEFAULT_COUNTRY)
  // The placeholder keeps typing while the field is empty, caret in it or
  // not, and the first typed character ends it.
  const placeholder = useTypedPlaceholder(
    COST_BOM_DEMO_STRINGS.composerStem,
    COST_BOM_DEMO_STRINGS.composerExamples,
    text !== '',
  )

  useEffect(() => {
    setProductImage(DEFAULT_PRODUCT)
  }, [])

  const typed = text.trim().length > 0

  const submit = () => {
    triggerTagManagerEvent(
      TagManagerEventType.costModelRegisterClick,
      typed
        ? TagManagerAction.heroSubmitWithProduct
        : TagManagerAction.heroSubmitEmpty,
    )
    onSubmit({ product: text.trim(), sourcing: country })
  }

  return (
    <div className="hero-composer">
      <textarea
        className="hero-composer-input"
        rows={2}
        value={text}
        placeholder={placeholder}
        aria-label={COST_BOM_DEMO_STRINGS.composerPlaceholder}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          // Enter sends, as in any chat composer; shift keeps the newline.
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            submit()
          }
        }}
      />

      <div className="hero-composer-row">
        <HeroSourcingSelect value={country} onChange={setCountry} />

        <button
          type="button"
          className="hero-composer-send"
          id="hero-composer-send"
          onClick={submit}
        >
          <span className="hero-composer-send__label">
            {COST_BOM_DEMO_STRINGS.composerSend}
          </span>
          <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M3 8h10M9 4l4 4-4 4"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* The engine reads the product and sourcing from these when it builds. */}
      <input
        type="hidden"
        id="hero-dd-product"
        value={DEFAULT_PRODUCT}
        readOnly
      />
      <input type="hidden" id="hero-dd-country" value={country} readOnly />
    </div>
  )
}
